import React, {useRef, useState, useCallback, useEffect} from 'react';
import {sleep} from 'utils/sleep.js';

const CHAR_HEIGHT = 22;
const CHAR_WIDTH = 9;
export const useSyntaxHelper=({ref, code, onChange, doc, skipMouse=false, timeout=250, disable}) => {
    const [possibilities, setPossibilities] = useState([]);
    const [library, setLibrary] = useState(undefined);
    const [selectedChoice, setSelectedChoice] = useState(undefined);
    const [matchingParenthesis, setMatchingParenthesis] = useState(undefined);
    const counter = useRef(0);

    useEffect(() => {
        if (disable) {
            return;
        }
        if (!ref || !ref.current) {return undefined;}
        ref.current.addEventListener("keydown", onKeyDown);
        ref.current.addEventListener("keyup", handleChange);
        if (!skipMouse) {
            ref.current.addEventListener("mouseup", handleChange);
        }
        return () => {
            if (!ref.current) {
                return;
            }
            ref.current.removeEventListener("keydown", onKeyDown);
            ref.current.removeEventListener("keyup", handleChange);
            if (!skipMouse) {
                ref.current.removeEventListener("mouseup", handleChange);
            }
        };
    }, [code, library, possibilities, selectedChoice, setSelectedChoice, setLibrary, disable]);

    const onKeyDown = useCallback((e) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            if (selectedChoice) {
                choosePossibility(selectedChoice);
            }
            return false;
        } 
        return true;
    }, [code, onChange, possibilities, setPossibilities, possibilities, selectedChoice, setSelectedChoice]);

    const handleParenthesis = useCallback((caret) => {
        // okay so basically we need to look backwards until we find the matching
        // parenthesis and set that 
        let parens = 1;
        caret-=2;
        for (; caret >= 0 && parens > 0; caret--) {
            if (code.charAt(caret) === ')') {
                parens++;
            }  else if (code.charAt(caret) === '(') {
                parens--;
            }
        }

        if (caret > 0) {
            // then we have the matching parenthesis and its
            // at this position
            // we need to find the x/y position
            let {x,y} = getCaretPos(caret, code);
            setMatchingParenthesis({x,y});
            setTimeout(() => {
                setMatchingParenthesis(undefined);
            }, 1000);
        }
    }, [setMatchingParenthesis, code]);

    const handleChange = useCallback(async (e) => {
        if (!ref.current) {
            return;
        }
        if (disable) {
            return;
        }
        counter.current++;
        let _counter = counter.current;
        await sleep(timeout);
        if (_counter !== counter.current) {
            return;
        }
        if (e.key === 'Tab') {
            return false;
        }
        // get the caret position
        let c = ref.current.selectionStart;
        if (e.key === ')') {
            // then we need to talk parenthesis
            handleParenthesis(c);
        }

        let _code = code.replaceAll("\(", " ").replaceAll("\)", " ");
        let word =  getWordAt(_code, c);
        if (word === '') {
            word = getWordAt(_code, c-1);
        }
        if (word.trim() === '') {
            if (possibilities.length > 0) {
                setPossibilities([]);
            }
            if (library) {
                setLibrary(undefined);
            }
            return;
        }

        if (word === "load" || word === "library") {
            let library =  getNextQuoteString(_code, c);
            if (library !== "") {
                setLibrary(library);
                return;
            }
        }

        let keywords = doc().filter(
            x => x.startsWith(word));

        if (keywords.length === 1 && keywords[0] === word.trim()) {
            if (keywords.length > 0) {
                setPossibilities([]);
            }
            return;
        }

        // find all possiblities
        setPossibilities(keywords);
        setSelectedChoice(keywords.find(x => x === word) || keywords[0]);
    }, [code, library, possibilities, setPossibilities, setSelectedChoice, setLibrary, disable]);

    const choosePossibility = useCallback((choice) => {
        let start = getWordStart(code, ref.current.selectionStart);
        let end = getWordEnd(code, ref.current.selectionStart);
        let pre = code.substring(0, start === 0 ? 0 : start + 1);
        let post = code.substring(end);
        onChange({target: {value: pre + choice + post}});
        setTimeout(() => {
            let pos = pre.length  + choice.length;
            if (ref.current) {
                ref.current.focus();
                ref.current.setSelectionRange(pos, pos);
                setPossibilities([]);
                setLibrary(undefined);
            }
        }, 50);
    }, [code, onChange, setLibrary, setPossibilities]);
    return {
        choosePossibility,
        library,
        possibilities,
        setPossibilities,
        setLibrary,
        selectedChoice,
        matchingParenthesis};
};

const getWordStart = (str, pos) => {
    pos--;
    while (pos > 0 &&
           str.charAt(pos) !== ' ' &&
           str.charAt(pos) !== '\n' && 
           str.charAt(pos) !== ',' &&
           str.charAt(pos) !== ')' &&
           str.charAt(pos) !== '(') {
        pos--;
    }
    return pos;
};

const getWordEnd = (str, pos) => {
    pos--;
    while (pos < str.length &&
           str.charAt(pos) !== ' ' && 
           str.charAt(pos) !== '\n' && 
           str.charAt(pos) !== ',' &&
           str.charAt(pos) !== ')' &&
           str.charAt(pos) !== '(') {
        pos++;
    }
    return pos;
};

const getNextQuoteString = (str, pos) => {
    // startingg at pos find the next two quotes
    let quote = "";
    let starting = false;
    for (let i=pos; i < str.length; i++ ){
        if (str.charAt(i) === '\"') {
            if (starting) {
                return quote;
            }
            starting = true;
            continue;
        }
        if (starting) {
            quote += str.charAt(i);
        }
    }
    return quote;
};

const getWordAt = (str, pos) => {

    // Perform type conversions.
    str = String(str);
    pos = Number(pos) >>> 0;

    // Search for the word's beginning and end.
    var left = str.slice(0, pos + 1).search(/\S+$/),
        right = str.slice(pos).search(/\s/);

    // The last word in the string is a special case.
    if (right < 0) {
        return str.slice(left);
    }

    // Return the word, using the located bounds to extract it from the string.
    return str.slice(left, right + pos);

}

const setCaretPosition = (ref, caretPos) => {
    var elem = ref.current;

    if(elem != null) {
        if(elem.createTextRange) {
            var range = elem.createTextRange();
            range.move('character', caretPos);
            range.select();
        }
        else {
            if(elem.selectionStart) {
                elem.focus();
                elem.setSelectionRange(caretPos, caretPos);
            }
            else
                elem.focus();
        }
    }
}

const getCaretPos = (caret, code) => {
    // first get what line its on
    caret++;
    code = code.slice(0, caret+1);
    let lines = code.split("\n");
    let numLines = lines.length - 1;
    let preTotal = lines.slice(0, lines.length - 1).reduce(
        (a,b) => a + b.length + 1, 0);
    let x = (caret - preTotal)*CHAR_WIDTH;
    let y = numLines*CHAR_HEIGHT;
    return {x,y};
};
